'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

const RadioGroupContext = React.createContext<{ name: string } | null>(null);

const labelBaseClass =
  'inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-navy-dark select-none rounded-sm focus-within:outline-none focus-within:ring-2 focus-within:ring-cyan focus-within:ring-offset-2 focus-within:ring-offset-white';

export interface RadioGroupProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'role'> {
  /** `name` compartilhado por todos os `Radio` filhos (agrupamento nativo). */
  name: string;
}

/**
 * Container acessível para opções mutuamente exclusivas (`role="radiogroup"`).
 * Envolver os {@link Radio} filhos; o `name` do grupo é injetado via contexto.
 */
export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  { name, className, children, 'aria-labelledby': ariaLabelledby, 'aria-label': ariaLabel, ...props },
  ref
) {
  const ctx = React.useMemo(() => ({ name }), [name]);

  return (
    <RadioGroupContext.Provider value={ctx}>
      <div
        ref={ref}
        role="radiogroup"
        aria-labelledby={ariaLabelledby}
        aria-label={ariaLabel}
        className={cn('flex flex-row flex-wrap items-center gap-x-4 gap-y-2', className)}
        {...props}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
});
RadioGroup.displayName = 'RadioGroup';

export interface RadioProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type' | 'children'> {
  /** Texto ou nó ao lado do controle (o `label` envolve o input). */
  label: React.ReactNode;
  /**
   * Sobrescreve o `name` do grupo (use só fora de `RadioGroup` ou casos especiais).
   * Dentro de `RadioGroup`, o `name` vem do contexto.
   */
  name?: string;
  /** Classes extras no `<label>`. */
  labelClassName?: string;
  /** Classes na bolha visual (substitui estilos padrão do indicador). */
  indicatorClassName?: string;
}

/**
 * Opção de rádio com rótulo integrado; indicador customizado (evita recorte do controle nativo).
 * Estado selecionado: `border-cyan` + `bg-cyan` (círculo único, sem ponto interno).
 */
export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, labelClassName, indicatorClassName, className, id, disabled, name: nameProp, ...inputProps },
  ref
) {
  const ctx = React.useContext(RadioGroupContext);
  const resolvedName = nameProp ?? ctx?.name;
  if (resolvedName == null || resolvedName === '') {
    throw new Error('Radio: informe `name` na prop ou use dentro de <RadioGroup name="...">.');
  }

  const autoId = React.useId();
  const stableSlug =
    inputProps.value != null && String(inputProps.value).length > 0 ?
      String(inputProps.value).replace(/\s+/g, '-')
    : autoId.replace(/:/g, '');
  const inputId = id ?? `${resolvedName}-${stableSlug}`;

  const checked = inputProps.checked === true;

  return (
    <label
      htmlFor={inputId}
      className={cn(labelBaseClass, disabled && 'cursor-not-allowed opacity-50', labelClassName, className)}
    >
      <input
        ref={ref}
        id={inputId}
        type="radio"
        name={resolvedName}
        disabled={disabled}
        className="sr-only"
        {...inputProps}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-foreground transition-[background-color,border-color]',
          checked ? 'static-gradient-bg' : 'bg-muted',
          indicatorClassName
        )}
      >
        {/* {checked ?
          <span className="h-1 w-1 rounded-full bg-white shadow-sm" aria-hidden />
        : null} */}
      </span>
      <span className="min-w-0">{label}</span>
    </label>
  );
});
Radio.displayName = 'Radio';
